import {
  ANDROID_PACKAGE,
  APP_NAME,
  FB_APP_ID,
  FB_PAGES,
  IOS_APP_STORE_ID,
  ITUNES_APP,
  OG_IMAGE,
  OG_SITE_NAME,
  ROBOTS,
  TWITTER_CARD,
} from "@elements/MetaTags/MetaTagsConstants";
import { BH_BASE_URL } from "@helper/getEnvVariables";
import type {
  ExtendedMetaDescriptor,
  MetaTagsProps as SEOMetaTagsProps,
} from "@sphtech/web2-seo-schema";
import {
  createClientSideMetaTags,
  createMetaTags,
} from "@sphtech/web2-seo-schema";
import type { GADataProps } from "@typings/GA";
import { MetaTagsProps } from "@typings/MetaTags";

function buildMetaTagsProps(metaTags: MetaTagsProps): SEOMetaTagsProps {
  const origin = BH_BASE_URL.replace(/\/$/, "");
  const resolvedSlug = metaTags.slug === "home" ? "" : metaTags.slug || "";

  const canonicalUrl: string = metaTags.customCanonicalUrl
    ? origin + metaTags.customCanonicalUrl
    : origin + resolvedSlug;

  const image: string = metaTags.image?.url
    ? String(metaTags.image.url)
    : BH_BASE_URL + OG_IMAGE;
  const ogImage: string = metaTags.ogImage?.url
    ? String(metaTags.ogImage.url)
    : BH_BASE_URL + OG_IMAGE;

  const additionalMetaDescriptors: ExtendedMetaDescriptor[] = [
    // BH-specific Facebook pages tag (package only has fb:admins)
    { property: "fb:pages", content: FB_PAGES },
    // Extended og:image tags not in package
    { property: "og:image:url", content: ogImage },
    { property: "og:image:secure_url", content: ogImage },
    {
      property: "og:image:width",
      content: metaTags.ogImage?.width ? String(metaTags.ogImage.width) : "",
    },
    {
      property: "og:image:height",
      content: metaTags.ogImage?.height ? String(metaTags.ogImage.height) : "",
    },
    // Manifest and image_src links
    { tagName: "link", rel: "manifest", href: "/manifest.json" },
    ...(image
      ? [
          {
            tagName: "link",
            rel: "image_src",
            href: image,
            "data-rh": "true",
          } as ExtendedMetaDescriptor,
        ]
      : []),
  ];

  if (metaTags.modifiedTime) {
    additionalMetaDescriptors.push({
      property: "og:updated_time",
      content: metaTags.modifiedTime,
    });
  }

  if (metaTags.isArticlePage) {
    additionalMetaDescriptors.push(
      { name: "dcterms.title", content: metaTags.title },
      { name: "dcterms.creator", content: "cue" },
      { name: "dcterms.description", content: metaTags.description || "" },
      { name: "dcterms.date", content: metaTags.publishedTime || "" },
      { name: "dcterms.type", content: "text" },
      { name: "dcterms.format", content: "text/html" },
      { name: "dcterms.identifier", content: canonicalUrl },
      {
        name: "article:published_time",
        content: metaTags.publishedTime || "",
      },
      { name: "article:modified_time", content: metaTags.modifiedTime || "" },
    );
  }

  if (metaTags.addAppleMobileTags) {
    additionalMetaDescriptors.push(
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-titlee", content: "Berita Harian" },
    );
  }

  if (metaTags.gsChannels && metaTags.gsChannels.length > 0) {
    additionalMetaDescriptors.push({
      name: "grapeshot_tag",
      content: metaTags.gsChannels.join(","),
    });
  }

  return {
    title: metaTags.title,
    description: metaTags.description || "",
    slug: resolvedSlug,
    urlOrigin: origin,
    ogSiteName: OG_SITE_NAME,
    ogType: metaTags.ogType,
    ogTitle: metaTags.ogTitle,
    ogDescription: metaTags.ogDescription,
    image: ogImage,
    publishedTime: metaTags.publishedTime,
    updatedTime: metaTags.modifiedTime,
    keywords: metaTags.keywords,
    robots: metaTags.robotsContent || metaTags.robots || ROBOTS,
    canonicalUrl,
    twitterCard: metaTags.twitterCardContent || TWITTER_CARD,
    isArticlePage: metaTags.isArticlePage,
    shortLink: metaTags.shortLink,
    fbAppId: FB_APP_ID,
    iosAppName: APP_NAME,
    iosAppStoreId: IOS_APP_STORE_ID,
    appleItunesAppId: ITUNES_APP,
    androidAppId: ANDROID_PACKAGE,
    includeAppleSmartBanner: true,
    additionalMetaDescriptors,
  };
}

export function getMetaData(metaTags: MetaTagsProps) {
  return createMetaTags(buildMetaTagsProps(metaTags));
}

export function updateClientSideMetaTags(metaTags: MetaTagsProps) {
  createClientSideMetaTags(buildMetaTagsProps(metaTags));
}

export function getGTMData(props: GADataProps): GADataProps {
  const {
    section = "",
    level2 = "",
    chapter1 = "", // Section child category name.
    chapter2 = "",
    chapter3 = "",
    at = "", // populated client-side via window.getCookieByName("mysphw")
    visitorcat = 0, // populated client-side via window.getCookieByName("visitorcat")
    svc = "", // populated client-side via window.getCookieByName("svc")
    title = "", // can contain spaces
    articleid = "",
    author = "", // Comma separated author name
    contentcat = "1", // Paid (2) or free (1)
    contenttype = "listing", // index/listing, article
    pubdate = "", // Post date of article otherwise ""
    keyword = "", // Comma separated keyword names
    pagination = 1,
    abVariant = "BH-DEFAULT",
    cue_articleid = "",
    cd_version = "bh2",
    cd_language = "ms",
    content_publication_date_utc = "",
    content_update_date_utc = "",
  } = props;

  const gaData: GADataProps = {
    section,
    chapter1,
    chapter2,
    chapter3,
    at,
    visitorcat,
    svc,
    abVariant,
    contenttype,
    title,
    contentcat,
    level2,
    keyword,
    author,
    pubdate,
    articleid,
    cue_articleid,
    cd_version,
    cd_language,
    pagination,
    content_publication_date_utc,
    content_update_date_utc,
  };

  return gaData;
}
